/**
 * Legal analysis view model: binds citations to the retained documents (and
 * the in-page viewer), separates material issues from ordinary obligations and
 * keeps processing states distinct. Presentation only; the current analysis
 * endpoint stays the result authority.
 */
import { safeDocumentUrl, type CurrentLegalAnalysis, type LegalActionStatus, type LegalApplicability, type LegalCheckpointId, type LegalCitation, type LegalCost, type LegalEvidenceStatus, type LegalPackArchiveSummary, type LegalPackDocumentSummary, type LegalPackPreparedAssets } from '../../../api/legal'

export type LegalReadiness = 'reviewed' | 'action_required' | 'blocked' | 'incomplete'

export interface LegalDocumentLink {
  id: string
  documentName?: string | null
  sourceUrl?: string | null
  preparedAssets?: LegalPackPreparedAssets | null
  processing?: LegalPackDocumentSummary['processing']
  status?: string | null
}

export interface CitationView {
  legalPackId: string
  documentName: string
  page: number | null
  clause: string | null
  quote: string | null
  sourceUrl: string | null
  /** The retained document opens in the in-page viewer when it is in this property's inventory. */
  viewer: { documentId: string; page: number | null } | null
}

export interface CheckpointView { checkpointId: LegalCheckpointId; detail: string; applicability: LegalApplicability; evidenceStatus: LegalEvidenceStatus; actionStatus: LegalActionStatus; citations: CitationView[] }

export interface FindingView {
  findingId: string
  title: string
  detail: string
  impact: string | null
  classification: 'routine' | 'material'
  severity: 'information' | 'action' | 'warning' | 'blocker'
  applicability: LegalApplicability
  evidenceStatus: LegalEvidenceStatus
  actionStatus: LegalActionStatus
  citations: CitationView[]
  owner: string | null
  deadline: string | null
  mitigation: string | null
  costLabel: string | null
}

export interface CostView { costId: string; label: string; amountPence: number | null; formula: LegalCost['formula']; status: LegalCost['status']; detail: string; citations: CitationView[] }

export interface LegalAnalysisViewModel {
  story: {
    property: string | null
    tenure: string | null
    titleNumbers: string[]
    occupancy: string | null
    sellers: string[]
    capacity: string | null
    registeredProprietor: boolean | null
    propertyCitations: CitationView[]
    sellerCitations: CitationView[]
    events: Array<{ type: string; date: string | null; detail: string; citations: CitationView[] }>
  } | null
  freshness: 'none' | 'current' | 'stale'
  stage: 'not_started' | 'queued' | 'complete' | 'failed'
  stageMessage: string | null
  failureCode: string | null
  archiveName: string | null
  documentCount: number
  documentsReviewed: number
  updatedAt: string | null
  reportReference: string | null
  headline: string | null
  legalReadiness: LegalReadiness
  unknowns: string[]
  checkpoints: CheckpointView[]
  findings: FindingView[]
  costs: CostView[]
}

function citationsFor(citations: LegalCitation[], documents: Map<string, LegalDocumentLink>): CitationView[] {
  // Finding, action and mitigation often cite the same excerpt; show each distinct reference once.
  const seen = new Set<string>()
  return citations.filter(citation => {
    const key = JSON.stringify([citation.legalPackId, citation.page, citation.clause, citation.quote])
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).map(citation => {
    const document = documents.get(citation.legalPackId)
    return {
      legalPackId: citation.legalPackId,
      documentName: document?.documentName ?? `Document ${citation.legalPackId.slice(0, 8)}`,
      page: citation.page,
      clause: citation.clause,
      quote: citation.quote,
      sourceUrl: safeDocumentUrl(document?.sourceUrl),
      viewer: document ? { documentId: document.id, page: citation.page } : null,
    }
  })
}

const pounds = (pence: number) => `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function costDetail(cost: LegalCost): string {
  const parts: string[] = []
  if (cost.formula === 'percent' || cost.formula === 'percent_with_minimum') parts.push(cost.rateBasisPoints === null ? 'Rate not stated' : `${(cost.rateBasisPoints / 100).toFixed(2)}%`)
  if (cost.percentageBasis) parts.push(`of ${cost.percentageBasis.replaceAll('_', ' ')}`)
  if (cost.percentageBasisAmountPence !== null) parts.push(`basis ${pounds(cost.percentageBasisAmountPence)}`)
  if (cost.minimumPence !== null) parts.push(`minimum ${pounds(cost.minimumPence)}`)
  parts.push(`VAT ${cost.vatTreatment.replaceAll('_', ' ')}`)
  if (cost.payee) parts.push(`payee ${cost.payee}`)
  if (cost.trigger) parts.push(cost.trigger)
  return parts.join(' · ')
}

function costLabel(cost: LegalCost): string {
  const amount = cost.amountPence === null ? null : pounds(cost.amountPence)
  const rate = cost.rateBasisPoints === null ? null : `${(cost.rateBasisPoints / 100).toFixed(2)}% of ${(cost.percentageBasis ?? 'unknown').replaceAll('_', ' ')}`
  if (amount && rate) return `${cost.label} ${rate} · stated amount ${amount}`
  if (amount) return `${cost.label} ${amount}`
  if (rate) return `${cost.label} ${rate}`
  return `${cost.label} unknown`
}

/** Every retained document across the user's archives; citations stay bound to the retained original. */
export function retainedDocuments(archives: LegalPackArchiveSummary[]): LegalDocumentLink[] {
  return archives.flatMap(archive => archive.documents.map(document => ({ id: document.id, documentName: document.documentName, sourceUrl: safeDocumentUrl(document.url), preparedAssets: document.preparedAssets })))
}

/** The documents of the archive the current analysis is bound to (or the archive selected for analysis). */
export function documentsForCurrentLegalPack(current: CurrentLegalAnalysis | null, archives: LegalPackArchiveSummary[], fallback: LegalDocumentLink[]): LegalDocumentLink[] {
  const activeArchive = archives.find(archive => archive.id === current?.archiveId) ?? archives.find(archive => archive.currentForAnalysis)
  if (!activeArchive) return fallback.map(document => ({ ...document, sourceUrl: safeDocumentUrl(document.sourceUrl) }))
  return activeArchive.documents.map(document => ({ id: document.id, documentName: document.documentName, sourceUrl: safeDocumentUrl(document.url), preparedAssets: document.preparedAssets, processing: document.processing, status: 'current_pack' }))
}

export function toLegalAnalysisViewModel(current: CurrentLegalAnalysis, archives: LegalPackArchiveSummary[], documentLinks: LegalDocumentLink[]): LegalAnalysisViewModel {
  const documentMap = new Map([...documentLinks, ...retainedDocuments(archives)].map(document => [document.id, document]))
  const archive = archives.find(candidate => candidate.id === current.archiveId) ?? archives.find(candidate => candidate.currentForAnalysis) ?? null
  const analysis = current.state === 'current' ? current.analysis : null
  const freshness = current.state === 'current' ? 'current' : current.state === 'stale' ? 'stale' : 'none'
  const stage = current.state === 'current' ? 'complete' : current.state === 'pending' ? 'queued' : current.state === 'failed' ? 'failed' : 'not_started'

  const findings: FindingView[] = (analysis?.findings ?? []).map(finding => {
    const action = (analysis?.actions ?? []).find(candidate => candidate.status === 'open' && candidate.relatedFindingIds.includes(finding.findingId)) ?? null
    const mitigation = (analysis?.mitigations ?? []).find(candidate => candidate.status === 'open' && candidate.relatedFindingIds.includes(finding.findingId)) ?? null
    const relatedCosts = (analysis?.costs ?? []).filter(cost => cost.relatedFindingIds.includes(finding.findingId) || (action && cost.relatedActionIds.includes(action.id)))
    return {
      findingId: finding.findingId, title: finding.title, detail: finding.detail, impact: finding.impact, classification: finding.classification, severity: finding.severity,
      applicability: finding.applicability, evidenceStatus: finding.evidenceStatus, actionStatus: finding.actionStatus,
      citations: citationsFor([...finding.citations, ...(action?.citations ?? []), ...(mitigation?.citations ?? [])], documentMap),
      owner: action?.responsibleParty ?? mitigation?.responsibleParty ?? null,
      deadline: action?.dueBy ?? mitigation?.dueBy ?? null,
      mitigation: mitigation?.detail ?? null,
      costLabel: relatedCosts.length ? relatedCosts.map(costLabel).join(' · ') : null,
    }
  })

  return {
    story: analysis ? {
      property: analysis.propertyContext.address ?? null,
      tenure: analysis.propertyContext.tenure ?? null,
      titleNumbers: analysis.propertyContext.titleNumbers,
      occupancy: analysis.propertyContext.occupancy ?? null,
      sellers: analysis.sellerContext.names,
      capacity: analysis.sellerContext.capacity ?? null,
      registeredProprietor: analysis.sellerContext.registeredProprietor ?? null,
      propertyCitations: citationsFor(analysis.propertyContext.citations, documentMap),
      sellerCitations: citationsFor(analysis.sellerContext.citations, documentMap),
      events: analysis.history.map(event => ({ type: event.eventType, date: event.eventDate ?? null, detail: event.detail, citations: citationsFor(event.citations, documentMap) })),
    } : null,
    freshness,
    stage,
    failureCode: current.state === 'failed' ? current.processingFailure?.code ?? null : null,
    stageMessage: current.state === 'pending'
      ? 'The latest pack is waiting in the managed analysis queue or an attempt is in progress. A fingerprint-current result will appear here when it is published.'
      : current.state === 'failed'
        ? current.processingFailure?.message ?? 'The worker did not complete a valid analysis. No legal conclusion was published.'
        : current.state === 'ready'
          ? 'Your pack is ready. Request legal analysis to review these documents.'
          : current.state === 'missing' && archive
            ? 'No legal review has been published for your selected pack.'
            : current.state === 'missing'
              ? 'Upload a complete legal pack to begin a review.'
              : null,
    archiveName: archive?.archiveName ?? null,
    documentCount: archive?.documentCount ?? documentLinks.length,
    documentsReviewed: analysis?.documents.length ?? 0,
    updatedAt: current.updatedAt,
    reportReference: current.state === 'current' && current.reportId ? current.reportId.slice(0, 8) : null,
    headline: analysis?.summary.headline ?? null,
    legalReadiness: analysis?.summary.legalReadiness ?? 'incomplete',
    unknowns: analysis?.summary.unknowns ?? [],
    checkpoints: (analysis?.checkpoints ?? []).map(checkpoint => ({ ...checkpoint, citations: citationsFor(checkpoint.citations, documentMap) })),
    findings,
    costs: (analysis?.costs ?? []).map(cost => ({ costId: cost.costId, label: cost.label, amountPence: cost.amountPence, formula: cost.formula, status: cost.status, detail: costDetail(cost), citations: citationsFor(cost.citations, documentMap) })),
  }
}

/** A material finding that still needs an action, as opposed to an ordinary obligation or a resolved matter. */
export function requiresAction(item: FindingView): boolean {
  return item.classification === 'material'
    && item.applicability !== 'does_not_apply'
    && (['action', 'warning', 'blocker'].includes(item.severity) || ['clarification', 'specialist_resolution', 'blocker'].includes(item.actionStatus))
    && !['none', 'resolved'].includes(item.actionStatus)
}

export function partitionLegalFindings(items: FindingView[]) {
  const actions: FindingView[] = [], findings: FindingView[] = [], routine: FindingView[] = []
  for (const item of items) {
    if (requiresAction(item)) actions.push(item)
    else if (item.classification === 'routine') routine.push(item)
    else findings.push(item)
  }
  return { actions, findings, routine }
}

/** Page text between generated page markers. Never PDF printed folios or an inferred offset. */
export function markdownForLegalPage(markdown: string, documentId: string, page: number): string | null {
  const markers = [...markdown.matchAll(/<!-- legal-page: documentId=([^;]+); page=(\d+);[^>]*-->/g)]
  const index = markers.findIndex(match => match[1] === documentId && Number(match[2]) === page)
  const marker = markers[index]
  if (!marker) return null
  return markdown.slice(marker.index! + marker[0].length, markers[index + 1]?.index ?? markdown.length).trim()
}
